import type { DocIngestStatus, VatCreditStatus } from '@dcc-erp/shared';

import type { ReactNode } from 'react';

export interface ReviewHeaderStripProps {
  status: DocIngestStatus;
  verifiedCount: number;
  totalFields: number;
  vatCreditStatus: VatCreditStatus | null;
  onVatCreditChange?: (v: VatCreditStatus) => void;
  readOnly?: boolean;
  vendorSummary?: ReactNode;
}

// Thai label + .badge.* variant per DocIngestStatus.
const STATUS_BADGE: Record<DocIngestStatus, { label: string; variant: string }> = {
  UPLOADED: { label: 'อัปโหลดแล้ว', variant: 'neutral' },
  EXTRACTING: { label: 'กำลังอ่านข้อมูล', variant: 'info' },
  EXTRACTED: { label: 'อ่านข้อมูลแล้ว', variant: 'info' },
  REVIEWING: { label: 'กำลังตรวจสอบ', variant: 'brand' },
  PENDING_APPROVAL: { label: 'รออนุมัติ 4 ตา', variant: 'warning' },
  POSTED: { label: 'ลงบัญชีแล้ว', variant: 'positive' },
  REJECTED: { label: 'ปฏิเสธแล้ว', variant: 'neutral' },
  FAILED: { label: 'ล้มเหลว', variant: 'negative' },
};

const VAT_OPTIONS: readonly (readonly [VatCreditStatus, string])[] = [
  ['CLAIMABLE', 'ขอคืนภาษีได้'],
  ['SUSPENSE', 'ภาษีซื้อรอเครดิต'],
];

export function ReviewHeaderStrip({
  status,
  verifiedCount,
  totalFields,
  vatCreditStatus,
  onVatCreditChange,
  readOnly = false,
  vendorSummary,
}: ReviewHeaderStripProps) {
  const badge = STATUS_BADGE[status];
  return (
    <div className="review-strip">
      <div className="review-strip__vendor">{vendorSummary}</div>

      {/* §14: ONE pre-rendered aria-live=polite region for the X/Y counter
          (the accept-result text is appended here by a later phase). */}
      <div className="review-strip__progress" aria-live="polite">
        <span className="muted" style={{ fontSize: 13 }}>
          ตรวจแล้ว {verifiedCount}/{totalFields} ช่อง
        </span>
      </div>

      <div
        className="review-strip__vat tabs"
        role="group"
        aria-label="สถานะเครดิตภาษีซื้อ"
        style={{ margin: 0, borderBottom: 'none' }}
      >
        {VAT_OPTIONS.map(([value, label]) => {
          const active = vatCreditStatus === value;
          return (
            <button
              key={value}
              type="button"
              className={`tab ${active ? 'active' : ''}`}
              aria-pressed={active}
              disabled={readOnly}
              onClick={() => onVatCreditChange?.(value)}
            >
              {label}
            </button>
          );
        })}
      </div>

      <div className="review-strip__status">
        <span className={`badge ${badge.variant}`}>
          <span className="bdot" />
          {badge.label}
        </span>
      </div>
    </div>
  );
}
